import React from "react";

const NODE_TYPES = [
  {
    value: "module",
    label: "Module",
    description: "Container such as cabinet, drawer module, assembly.",
  },
  {
    value: "element",
    label: "Element",
    description: "Physical part such as board, door, panel, profile.",
  },
  {
    value: "hardware",
    label: "Hardware",
    description: "Catalog item such as hinge, runner, handle, connector.",
  },
  {
    value: "project",
    label: "Project",
    description: "Top-level project definition.",
  },
];

const CATEGORY_PRESETS = {
  module: [
    "cabinet.base",
    "cabinet.wall",
    "cabinet.tall",
    "drawer.system",
    "assembly.generic",
  ],
  element: [
    "board.side",
    "board.top",
    "board.bottom",
    "board.back",
    "board.shelf",
    "front.door",
    "front.drawer",
    "profile.edge",
    "profile.handle",
  ],
  hardware: [
    "hardware.hinge",
    "hardware.runner",
    "hardware.lift",
    "hardware.handle",
    "hardware.connector",
    "processing.drilling",
  ],
  project: ["project.interior", "project.kitchen", "project.wardrobe"],
};

function FieldHint({ children }) {
  return <div className="mt-1 text-xs text-gray-500">{children}</div>;
}

function SectionTitle({ children }) {
  return <div className="text-sm font-semibold text-gray-700">{children}</div>;
}

export default function DefinitionPropertiesInspector({
  name,
  code,
  description,

  nodeType,
  category,
  geometryType,

  onChangeName,
  onChangeCode,
  onChangeDescription,
  onChangeNodeType,
  onChangeCategory,
  onChangeGeometryType,
}) {
  const currentNodeType = nodeType || "element";
  const categoryPresets = CATEGORY_PRESETS[currentNodeType] || [];

  const allowedGeometryTypes =
    currentNodeType === "hardware"
      ? ["none", "catalog_glb"]
      : ["none", "box", "profile_extrude"];

  const safeGeometryType = allowedGeometryTypes.includes(geometryType)
    ? geometryType
    : "none";

  function handleNodeTypeChange(nextType) {
    onChangeNodeType?.(nextType);

    if (nextType === "hardware") {
      onChangeGeometryType?.("none");
    }

    if (nextType === "module") {
      onChangeGeometryType?.("none");
    }
  }

  return (
    <div className="space-y-4">

    <div className="border rounded p-3 bg-white space-y-3">
      <div>
        <SectionTitle>Basic info</SectionTitle>
        <FieldHint>
          Name, code and description for this definition.
        </FieldHint>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">
          Name
        </label>
        <input
          value={name || ""}
          onChange={(e) => onChangeName?.(e.target.value)}
          placeholder="Definition name"
          className="w-full px-3 py-2 border rounded"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">
          Code
        </label>
        <input
          value={code || ""}
          onChange={(e) => onChangeCode?.(e.target.value)}
          placeholder="definition_code"
          className="w-full px-3 py-2 border rounded"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">
          Description
        </label>
        <textarea
          value={description || ""}
          onChange={(e) => onChangeDescription?.(e.target.value)}
          placeholder="Description"
          className="w-full px-3 py-2 border rounded h-20"
        />
      </div>
    </div>

      <div>
        <SectionTitle>Definition Type</SectionTitle>
        <FieldHint>
          This controls how the definition behaves in the hierarchy and editor.
        </FieldHint>
      </div>

      <div className="grid grid-cols-1 gap-2">
        {NODE_TYPES.map((type) => {
          const active = currentNodeType === type.value;

          return (
            <button
              key={type.value}
              type="button"
              onClick={() => handleNodeTypeChange(type.value)}
              className={`text-left border rounded p-3 transition ${
                active
                  ? "bg-blue-50 border-blue-300"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="font-medium">{type.label}</div>
                <div className="text-xs text-gray-400">{type.value}</div>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {type.description}
              </div>
            </button>
          );
        })}
      </div>

      <div className="border-t pt-4 space-y-3">
        <div>
          <SectionTitle>Category</SectionTitle>
          <FieldHint>
            Use structured categories like <code>board.side</code> or{" "}
            <code>hardware.hinge</code>.
          </FieldHint>
        </div>

        <input
          value={category || ""}
          onChange={(e) => onChangeCategory?.(e.target.value)}
          placeholder="cabinet.base"
          list="definition-category-presets"
          className="w-full px-3 py-2 border rounded"
        />

        <datalist id="definition-category-presets">
          {categoryPresets.map((preset) => (
            <option key={preset} value={preset} />
          ))}
        </datalist>

        {categoryPresets.length ? (
          <div className="flex flex-wrap gap-2">
            {categoryPresets.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => onChangeCategory?.(preset)}
                className={`px-2 py-1 rounded border text-xs ${
                  category === preset
                    ? "bg-blue-50 border-blue-300 text-blue-700"
                    : "bg-white"
                }`}
              >
                {preset}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="border-t pt-4 space-y-3">
        <div>
          <SectionTitle>Geometry</SectionTitle>
          <FieldHint>
            Box/profile draws generated geometry. Hardware usually uses catalog
            GLB through the Catalog tab.
          </FieldHint>
        </div>

        <select
          value={safeGeometryType}
          onChange={(e) => onChangeGeometryType?.(e.target.value)}
          className="w-full px-3 py-2 border rounded"
        >
          {allowedGeometryTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>

        {currentNodeType === "hardware" ? (
          <div className="border rounded p-3 bg-amber-50 text-xs text-amber-800">
            Hardware geometry is normally loaded from the linked catalog item
            asset. Set the catalog item in the Catalog tab.
          </div>
        ) : null}

        {currentNodeType === "module" ? (
          <div className="border rounded p-3 bg-purple-50 text-xs text-purple-800">
            Modules are usually containers, so geometry is normally{" "}
            <strong>none</strong>. Add boards/elements as children.
          </div>
        ) : null}
      </div>
    </div>
  );
}